import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client.js';
import { realtime, type StatusDuration } from './realtime.js';

// ─── Tickets ──────────────────────────────────────────────────────────

export interface TicketRow {
  id: string;
  number: number;
  contactId: string;
  channelId: string;
  /** Магазин диалога (вкладки входящих, подпись в общем списке) */
  storeId?: string | null;
  groupId: string | null;
  assignedTo: string | null;
  status: string;
  priority: string;
  subject: string | null;
  tags: string[];
  slaDeadline: string | null;
  slaBreached: boolean;
  firstReplyAt: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  /** Последнее публичное сообщение в тикете — от клиента. Тикет «ждёт ответа». */
  awaiting_reply?: boolean;
}

export function useTicketList(
  workspaceSlug: string | undefined,
  filter: Record<string, string | string[] | undefined>,
) {
  // Multi-value фильтры (status[], priority[], tag[]) сериализуем как
  // повторяющиеся ключи `status=new&status=open` — Fastify+zod парсят их в массив.
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      for (const x of v) if (x) params.append(k, x);
    } else {
      params.append(k, v);
    }
  }
  const qs = params.toString();
  return useQuery({
    queryKey: ['tickets', workspaceSlug, qs],
    queryFn: () => apiClient.get<TicketRow[]>(`/tickets${qs ? '?' + qs : ''}`, { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

export function useTicket(workspaceSlug: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ticket', workspaceSlug, id],
    queryFn: () => apiClient.get<TicketRow>(`/tickets/${id}`, { workspaceSlug }),
    enabled: !!workspaceSlug && !!id,
  });
}

export function useCreateTicket(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      contact_id: string;
      channel_id: string;
      subject?: string;
      priority?: string;
      tags?: string[];
    }) => apiClient.post<TicketRow>('/tickets', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] }),
  });
}

export function useUpdateTicket(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<TicketRow>(`/tickets/${id}`, patch, { workspaceSlug }),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['ticket', workspaceSlug, vars.id] });
    },
  });
}

export function useAssignTicket(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      apiClient.post(`/tickets/${id}/assign`, { user_id: userId }, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] }),
  });
}

// ─── Messages ─────────────────────────────────────────────────────────

export interface MessageRow {
  id: string;
  ticketId: string;
  direction: 'in' | 'out';
  senderKind: 'contact' | 'user' | 'bot' | 'system';
  senderUserId: string | null;
  senderContactId: string | null;
  body: string | null;
  visibility: 'public' | 'internal_note';
  /** Служебное: например, карточки товаров (`products`), см. commerce.schema */
  meta?: Record<string, unknown>;
  sentAt: string;
  deliveredAt: string | null;
  readAt: string | null;
  attachments?: Array<{ id: string; filename: string; mimeType: string; sizeBytes: number }>;
}

export function useMessages(workspaceSlug: string | undefined, ticketId: string | undefined) {
  return useQuery({
    queryKey: ['messages', workspaceSlug, ticketId],
    queryFn: () => apiClient.get<MessageRow[]>(`/tickets/${ticketId}/messages`, { workspaceSlug }),
    enabled: !!workspaceSlug && !!ticketId,
  });
}

export function useSendMessage(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticketId,
      body,
      visibility,
      attachments,
      meta,
      idempotencyKey,
    }: {
      ticketId: string;
      body: string;
      visibility?: 'public' | 'internal_note';
      attachments?: string[];
      meta?: Record<string, unknown>;
      idempotencyKey?: string;
    }) =>
      apiClient.post<MessageRow>(
        `/tickets/${ticketId}/messages`,
        { body, visibility, attachments, meta },
        { workspaceSlug, idempotencyKey },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['messages', workspaceSlug, vars.ticketId] });
      void qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] });
    },
  });
}

// ─── Contacts ─────────────────────────────────────────────────────────

export interface ContactRow {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  tags: string[];
  lastSeenAt: string | null;
  channelIds?: Record<string, string>;
  attributes?: { city?: string; segment?: string; total_orders?: number; total_amount?: number };
  createdAt?: string;
}

export function useContacts(workspaceSlug: string | undefined, query: string) {
  return useQuery({
    queryKey: ['contacts', workspaceSlug, query],
    queryFn: () =>
      apiClient.get<ContactRow[]>(`/contacts${query ? '?q=' + encodeURIComponent(query) : ''}`, {
        workspaceSlug,
      }),
    enabled: !!workspaceSlug,
  });
}

export function useContact(workspaceSlug: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['contact', workspaceSlug, id],
    queryFn: () => apiClient.get<ContactRow>(`/contacts/${id}`, { workspaceSlug }),
    enabled: !!workspaceSlug && !!id,
  });
}

export function useContactHistory(workspaceSlug: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['contact-history', workspaceSlug, id],
    queryFn: () => apiClient.get<TicketRow[]>(`/contacts/${id}/history`, { workspaceSlug }),
    enabled: !!workspaceSlug && !!id,
  });
}

export function useCreateContact(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; email?: string; phone?: string; tags?: string[] }) =>
      apiClient.post<ContactRow>('/contacts', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts', workspaceSlug] }),
  });
}

export function useUpdateContact(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<ContactRow>(`/contacts/${id}`, patch, { workspaceSlug }),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['contact', workspaceSlug, vars.id] });
      void qc.invalidateQueries({ queryKey: ['contacts', workspaceSlug] });
    },
  });
}

export function useDeleteContact(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/contacts/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts', workspaceSlug] }),
  });
}

export function useAnonymizeContact(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/contacts/${id}/anonymize`, {}, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts', workspaceSlug] }),
  });
}

// ─── Channels ─────────────────────────────────────────────────────────

export interface ChannelRow {
  id: string;
  type: string;
  name: string;
  external_identifier: string | null;
  is_active: boolean;
  config: Record<string, unknown>;
  /** Магазин, к которому относится канал; null — общий для всех */
  store_id: string | null;
  created_at: string;
}

export function useChannels(workspaceSlug: string | undefined): UseQueryResult<ChannelRow[]> {
  return useQuery({
    queryKey: ['channels', workspaceSlug],
    queryFn: () => apiClient.get<ChannelRow[]>('/channels', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

export function useCreateChannel(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: string;
      name: string;
      credentials: Record<string, unknown>;
      config?: Record<string, unknown>;
      store_id?: string | null;
    }) => apiClient.post<ChannelRow & { webhook_url?: string }>('/channels', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', workspaceSlug] }),
  });
}

/** Редактирование существующего канала (имя, активность, config, частичные credentials). */
export function useUpdateChannel(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<ChannelRow>(`/channels/${id}`, patch, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', workspaceSlug] }),
  });
}

/** Проверка УЖЕ сохранённого канала (использует хранимые доступы, ничего вводить не нужно). */
export function useTestChannel(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean; error?: string; details?: Record<string, unknown> }>(
        `/channels/${id}/test`,
        {},
        { workspaceSlug },
      ),
  });
}

/** Настройка входящего канала из кабинета (VK: регистрация Callback + подтверждение). */
export function useSetupChannel(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean; status: string; error?: string }>(
        `/channels/${id}/setup`,
        {},
        { workspaceSlug },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', workspaceSlug] }),
  });
}

/** Ручное объединение контактов: primary поглощает duplicate(ы). */
export function useMergeContacts(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { primary_id: string; duplicate_ids: string[] }) =>
      apiClient.post('/contacts/merge', body, { workspaceSlug }),
    onSuccess: () => {
      // Объединение переносит тикеты/каналы в primary — обновляем не только список,
      // но и карточку контакта + историю + тикеты (их читает панель инбокса).
      void qc.invalidateQueries({ queryKey: ['contacts', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['contact', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['contact-history', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['tickets', workspaceSlug] });
    },
  });
}

/** Удаление канала. */
export function useDeleteChannel(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/channels/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', workspaceSlug] }),
  });
}

/** Кросс-канальный ответ по SMS на телефон контакта (например, из формы). */
export function useReplySms(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, text }: { ticketId: string; text: string }) =>
      apiClient.post<{ ok: boolean; message_id: string }>(
        `/tickets/${ticketId}/reply-sms`,
        { text },
        { workspaceSlug },
      ),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['messages', workspaceSlug, v.ticketId] }),
  });
}

/** Запрос телефона у клиента (Telegram request_contact). */
export function useRequestPhone(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      apiClient.post<{ ok: boolean; message_id: string }>(
        `/tickets/${ticketId}/request-phone`,
        {},
        { workspaceSlug },
      ),
    onSuccess: (_d, ticketId) =>
      qc.invalidateQueries({ queryKey: ['messages', workspaceSlug, ticketId] }),
  });
}

/** Проверка credentials до создания канала (кнопки «Проверить …» в мастере). */
export function useTestChannelCredentials(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (body: { type: string; credentials: Record<string, unknown> }) =>
      apiClient.post<{ ok: boolean; error?: string; details?: Record<string, unknown> }>(
        '/channels/test-credentials',
        body,
        { workspaceSlug },
      ),
  });
}

// ─── Operators ────────────────────────────────────────────────────────

export interface OperatorRow {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  is_active: boolean;
  avatar_url: string | null;
  /** Разрешённые источники (типы каналов). [] = все. */
  allowed_sources?: string[];
}

export function useOperators(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['operators', workspaceSlug],
    queryFn: () => apiClient.get<OperatorRow[]>('/operators', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

export function useInviteOperator(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; name?: string; role?: string }) =>
      apiClient.post('/operators/invite', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators', workspaceSlug] }),
  });
}

/**
 * Mutation смены своего статуса. Только через WS (`status.set`) — REST
 * endpoint удалён. duration:
 *   'session'   — до закрытия вкладки/disconnect
 *   '15m'/'30m'/'45m'/'1h'/'3h' — авто-возврат в online через указанное время
 *   'persistent' — переживает reconnect, только ручной сброс отменит
 */
export function useSetMyStatus(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { status: 'online' | 'busy' | 'away'; duration?: StatusDuration }) => {
      const r = await realtime.setMyStatus(args.status, args.duration ?? 'session');
      if (!r.ok) throw new Error(r.error ?? 'status.set failed');
      return r;
    },
    // server рассылает operator.status_changed → useRealtimeInvalidation
    // уже invalidate-нет ['operators']. Локально форсим refetch на случай race.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators', workspaceSlug] }),
  });
}

// ─── Analytics ────────────────────────────────────────────────────────

export interface AnalyticsSummary {
  tickets_created: number;
  tickets_closed: number;
  tickets_resolved: number;
  sla_breaches: number;
  bot_transfers: number;
  avg_frt_ms: number | null;
  avg_resolution_ms: number | null;
}

export function useAnalyticsSummary(
  workspaceSlug: string | undefined,
  range: { from?: string; to?: string },
) {
  const qs = new URLSearchParams(range as Record<string, string>).toString();
  return useQuery({
    queryKey: ['analytics-summary', workspaceSlug, qs],
    queryFn: () =>
      apiClient.get<AnalyticsSummary>(`/analytics/summary${qs ? '?' + qs : ''}`, { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Bots ─────────────────────────────────────────────────────────────

export interface BotScenarioRow {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  version: number;
  flowJson: { start: string; nodes: Record<string, unknown> };
}

export function useBotScenarios(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['bot-scenarios', workspaceSlug],
    queryFn: () => apiClient.get<BotScenarioRow[]>('/bot-scenarios', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Widgets ──────────────────────────────────────────────────────────

export function useWidgets(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['widgets', workspaceSlug],
    queryFn: () => apiClient.get<unknown[]>('/widgets', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Onboarding ───────────────────────────────────────────────────────

export interface OnboardingState {
  currentStep?: number;
  completedSteps?: string[];
  completed?: boolean;
  totalSteps?: number;
  steps?: string[];
  checklist?: {
    account: boolean;
    channels: boolean;
    widget: boolean;
    operators: boolean;
    first_ticket: boolean;
  };
}

export function useOnboarding(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['onboarding', workspaceSlug],
    queryFn: () => apiClient.get<OnboardingState>('/onboarding', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── AI ───────────────────────────────────────────────────────────────

export function useAiSuggest(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: ({
      ticketId,
      tone,
    }: {
      ticketId: string;
      tone?: 'friendly' | 'formal' | 'concise';
    }) =>
      apiClient.post<{ text: string; model: string }>(
        `/ai/suggest-reply/${ticketId}`,
        { tone },
        { workspaceSlug },
      ),
  });
}

// ─── Bot flow editor ─────────────────────────────────────────────────

export function useUpdateBotFlow(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      flow,
    }: {
      id: string;
      flow: { start: string; nodes: Record<string, unknown> };
    }) =>
      apiClient.put<unknown>(`/bot-scenarios/${id}/flow`, { flow_json: flow }, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

// ─── Saved views ──────────────────────────────────────────────────────

export interface SavedView {
  id: string;
  name: string;
  icon: string | null;
  scope: 'system' | 'workspace' | 'personal';
  filters: Record<string, unknown>;
  sort: unknown[];
  pinned: boolean;
}

export function useSavedViews(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['saved-views', workspaceSlug],
    queryFn: () => apiClient.get<SavedView[]>('/saved-views', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Templates ──────────────────────────────────────────────────────

export interface TemplateRow {
  id: string;
  name: string;
  kind: 'form' | 'chat' | 'survey';
  category: string | null;
  config: Record<string, unknown>;
  isSystem: boolean;
}

export function useTemplates(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['templates', workspaceSlug],
    queryFn: () => apiClient.get<TemplateRow[]>('/templates', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Groups ──────────────────────────────────────────────────────────

export interface GroupRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

export function useGroups(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['groups', workspaceSlug],
    queryFn: () => apiClient.get<GroupRow[]>('/groups', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── SLA ────────────────────────────────────────────────────────────

export interface SLAPolicy {
  id: string;
  name: string;
  description: string | null;
  firstReplyMinutes: number;
  nextReplyMinutes: number | null;
  resolutionMinutes: number | null;
  businessHoursOnly: boolean;
  isActive: boolean;
  isDefault: boolean;
}

export function useSlaPolicies(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['sla', workspaceSlug],
    queryFn: () => apiClient.get<SLAPolicy[]>('/sla-policies', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Audit log ──────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  actorUserId: string | null;
  actorIp: string | null;
  actorUa: string | null;
  category: string;
  eventType: string;
  resourceType: string | null;
  resourceId: string | null;
  diff: Record<string, unknown>;
  occurredAt: string;
}

export function useAuditLog(
  workspaceSlug: string | undefined,
  params: Record<string, string> = {},
) {
  const qs = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ['audit', workspaceSlug, qs],
    queryFn: () =>
      apiClient.get<{ items: AuditLogEntry[]; total: number }>(`/audit${qs ? '?' + qs : ''}`, {
        workspaceSlug,
      }),
    enabled: !!workspaceSlug,
  });
}

// ─── Billing ────────────────────────────────────────────────────────

export interface SubscriptionInfo {
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_ends_at: string | null;
}
export interface UsageInfo {
  tickets_used: number;
  tickets_limit: number;
  operators_used: number;
  operators_limit: number;
  storage_used_bytes: number;
  storage_limit_bytes: number;
  data_connectors_used: number;
  data_connectors_limit: number;
}

export function useBillingSubscription(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['billing-subscription', workspaceSlug],
    queryFn: () => apiClient.get<SubscriptionInfo>('/billing/subscription', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}
export function useBillingUsage(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['billing-usage', workspaceSlug],
    queryFn: () => apiClient.get<UsageInfo>('/billing/usage', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── GDPR ────────────────────────────────────────────────────────────

export interface GdprRequest {
  id: string;
  contactId: string | null;
  kind: 'erasure' | 'export' | 'rectification' | 'objection';
  status: 'pending' | 'in_review' | 'approved' | 'completed' | 'rejected';
  requesterEmail: string | null;
  description: string | null;
  scheduledFor: string | null;
  completedAt: string | null;
  createdAt: string;
}

export function useGdprRequests(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['gdpr', workspaceSlug],
    queryFn: () => apiClient.get<GdprRequest[]>('/gdpr', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Webhooks ────────────────────────────────────────────────────────

export interface WebhookRow {
  id: string;
  name: string | null;
  url: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
}

export function useWebhooks(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['webhooks', workspaceSlug],
    queryFn: () => apiClient.get<WebhookRow[]>('/webhooks', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── API Keys ────────────────────────────────────────────────────────

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scope: string;
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export function useApiKeys(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['api-keys', workspaceSlug],
    queryFn: () => apiClient.get<ApiKeyRow[]>('/api-keys', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Data connectors ───────────────────────────────────────────────

export interface DataConnectorRow {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  auth: { type: string; base_url: string; link_id?: string };
  endpoints: Record<string, string>;
  display: { blocks?: string[]; orders_preview?: number };
  quick_actions: CommerceQuickAction[];
  cache_ttl_sec: number;
  rate_limit_per_min: number;
  timeout_ms: number;
  allow_mutations: boolean;
  is_active: boolean;
}

/** Готовое описание внешней системы — приходит с сервера, а не зашито в UI. */
export interface ConnectorPresetRow {
  id: string;
  title: string;
  description: string;
  icon: string;
  auth_mode: 'oauth_link' | 'paired' | 'manual_token';
  fields: Array<{
    key: string;
    label: string;
    placeholder?: string;
    hint?: string;
    required: boolean;
    secret?: boolean;
  }>;
  setup_hint: string | null;
  capabilities: string[];
  quick_actions: CommerceQuickAction[];
  defaults: {
    cache_ttl_sec: number;
    rate_limit_per_min: number;
    timeout_ms: number;
    allow_mutations: boolean;
  };
}

export interface CommerceQuickAction {
  id: string;
  label: string;
  scope: 'order' | 'product' | 'customer';
  template: string;
}

export interface CommerceMoney {
  amount: string;
  currency: string;
}

export interface CommerceCustomerRow {
  external_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  orders_count: number;
  total_spent: CommerceMoney | null;
  first_order_at: string | null;
  last_order_at: string | null;
  tags: string[];
  url: string | null;
}

export interface CommerceOrderRow {
  external_id: string;
  number: string | null;
  status: string;
  status_raw: string | null;
  total: CommerceMoney | null;
  created_at: string | null;
  items: Array<{
    title: string;
    sku: string | null;
    quantity: number;
    price: CommerceMoney | null;
    image_url: string | null;
    product_external_id: string | null;
  }>;
  delivery: {
    method: string | null;
    address: string | null;
    tracking_number: string | null;
    tracking_url: string | null;
    eta: string | null;
  } | null;
  payment_method: string | null;
  customer?: { name?: string | null; email?: string | null; phone?: string | null } | null;
  url: string | null;
}

export interface CommerceProductRow {
  external_id: string;
  title: string;
  sku: string | null;
  price: CommerceMoney | null;
  compare_at_price: CommerceMoney | null;
  in_stock: boolean | null;
  stock: number | null;
  image_url: string | null;
  url: string | null;
  description: string | null;
}

/** Всё, что нашлось по тикету во всех подключённых системах сразу. */
export interface CommerceContextRow {
  connector_id: string;
  connector_name: string;
  kind: string;
  display: { blocks?: string[]; orders_preview?: number };
  quick_actions: CommerceQuickAction[];
  customer: CommerceCustomerRow | null;
  /**
   * Как нашли покупателя и можно ли ему верить. Неподтверждённому сервер
   * уже скрыл адрес доставки и трек — панели остаётся объяснить почему.
   */
  match: {
    source: 'verified_identity' | 'email' | 'phone' | 'manual';
    confirmed: boolean;
  } | null;
  /** Нашлось несколько покупателей — выбирает оператор */
  candidates: CommerceCustomerRow[];
  orders: CommerceOrderRow[];
  /** Подборка «что предложить» — пусто, если источник такого не умеет. */
  recommendations: CommerceProductRow[];
  error?: string;
}

export function useDataConnectors(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['data-connectors', workspaceSlug],
    queryFn: () => apiClient.get<DataConnectorRow[]>('/data-connectors', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

export function useConnectorPresets(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['connector-presets', workspaceSlug],
    queryFn: () => apiClient.get<ConnectorPresetRow[]>('/data-connectors/presets', { workspaceSlug }),
    enabled: !!workspaceSlug,
    // Список пресетов меняется только с выходом новой версии.
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateDataConnector(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: string;
      name: string;
      auth: { type: string; base_url: string; username?: string; token_field?: string };
      secret?: string;
      endpoints: Record<string, string>;
      field_mapping?: Record<string, string>;
      display?: Record<string, unknown>;
      quick_actions?: CommerceQuickAction[];
      cache_ttl_sec?: number;
      rate_limit_per_min?: number;
      timeout_ms?: number;
      allow_mutations?: boolean;
    }) => apiClient.post<DataConnectorRow>('/data-connectors', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-connectors', workspaceSlug] }),
  });
}

export function useDeleteDataConnector(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ ok: true }>(`/data-connectors/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-connectors', workspaceSlug] }),
  });
}

export function useTestDataConnector(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean; status?: number; error?: string }>(
        `/data-connectors/${id}/test`,
        {},
        { workspaceSlug },
      ),
  });
}

/**
 * Данные покупателя по тикету. Грузится вместе с карточкой: оператор должен
 * увидеть заказы, ничего не нажимая.
 */
export function useCommerceContext(workspaceSlug: string | undefined, ticketId: string | undefined) {
  return useQuery({
    queryKey: ['commerce-context', workspaceSlug, ticketId],
    queryFn: () =>
      apiClient.get<CommerceContextRow[]>(`/data-connectors/context?ticket_id=${ticketId ?? ''}`, {
        workspaceSlug,
      }),
    enabled: !!workspaceSlug && !!ticketId,
    // Ответ уже кэширован на сервере; здесь короткий срок, чтобы переход
    // между тикетами не дёргал сеть на каждый клик.
    staleTime: 30_000,
    retry: false,
  });
}

/** «Это он» — оператор выбрал или подтвердил покупателя собеседника. */
export function useLinkCommerceCustomer(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      connectorId: string;
      ticketId: string;
      customer: Partial<CommerceCustomerRow>;
    }) =>
      apiClient.post<{ ok: true }>(
        `/data-connectors/${args.connectorId}/customer-link`,
        { ticket_id: args.ticketId, customer: args.customer },
        { workspaceSlug },
      ),
    onSuccess: (_r, vars) =>
      qc.invalidateQueries({ queryKey: ['commerce-context', workspaceSlug, vars.ticketId] }),
  });
}

/** «Это не он» — снять привязку, при следующем открытии поиск заново. */
export function useUnlinkCommerceCustomer(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { connectorId: string; ticketId: string }) =>
      apiClient.delete<{ ok: true }>(
        `/data-connectors/${args.connectorId}/customer-link?ticket_id=${args.ticketId}`,
        { workspaceSlug },
      ),
    onSuccess: (_r, vars) =>
      qc.invalidateQueries({ queryKey: ['commerce-context', workspaceSlug, vars.ticketId] }),
  });
}

/** Разовый запрос к внешней системе — поиск товара, поиск заказа. */
export function useCommerceQuery(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (args: {
      connectorId: string;
      type: string;
      params: Record<string, unknown>;
      ticketId?: string;
    }) =>
      apiClient.post<{ data: unknown; cached: boolean }>(
        `/data-connectors/${args.connectorId}/query`,
        { type: args.type, params: args.params, ticket_id: args.ticketId },
        { workspaceSlug },
      ),
  });
}

// ─── Связка аккаунтов с внешней платформой ─────────────────────────

export interface PartnerProviderRow {
  id: string;
  title: string;
  description: string;
  icon: string;
  /** oauth_link — связать в один клик; paired — кодом сопряжения из плагина */
  auth_mode: 'oauth_link' | 'paired';
  setup_hint: string | null;
  scopes: string[];
  /** Какие каналы платформа принимает кнопкой «Передать в …» */
  accepts_channel_types: string[];
}

export interface PartnerShopRow {
  id: string;
  name: string;
  url: string | null;
}

/** Поле, которого не хватило в ключе канала (ответ «ключ неполон»). */
export interface MissingCredentialField {
  key: string;
  label: string;
  hint?: string;
  secret: boolean;
  required: boolean;
}

export interface PartnerLinkRow {
  id: string;
  provider: string;
  baseUrl: string;
  externalAccountId: string;
  externalAccountName: string | null;
  scopes: string[];
  status: string;
  lastError: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export function usePartnerProviders(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['partner-providers', workspaceSlug],
    queryFn: () => apiClient.get<PartnerProviderRow[]>('/partner/providers', { workspaceSlug }),
    enabled: !!workspaceSlug,
    staleTime: 60 * 60 * 1000,
  });
}

export function usePartnerLinks(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['partner-links', workspaceSlug],
    queryFn: () => apiClient.get<PartnerLinkRow[]>('/partner/links', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

export function useConnectPartner(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (args: { provider: string; baseUrl: string; returnTo?: string }) =>
      apiClient.post<{ authorize_url: string }>(
        `/partner/${args.provider}/connect`,
        { base_url: args.baseUrl, return_to: args.returnTo },
        { workspaceSlug },
      ),
  });
}

export function useRevokePartnerLink(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ ok: true }>(`/partner/links/${id}`, { workspaceSlug }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['partner-links', workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ['data-connectors', workspaceSlug] });
    },
  });
}

// ─── Bot mutations ─────────────────────────────────────────────────

export function useCreateBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      apiClient.post<BotScenarioRow>('/bot-scenarios', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

export function useUpdateBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<BotScenarioRow>(`/bot-scenarios/${id}`, patch, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

export function useDeleteBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/bot-scenarios/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

export function useActivateBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<BotScenarioRow>(`/bot-scenarios/${id}/activate`, {}, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

export function useDeactivateBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<BotScenarioRow>(`/bot-scenarios/${id}/deactivate`, {}, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

export function useCloneBotScenario(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<BotScenarioRow>(`/bot-scenarios/${id}/clone`, {}, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-scenarios', workspaceSlug] }),
  });
}

// ─── Widget mutations ───────────────────────────────────────────────

export interface WidgetRow {
  id: string;
  workspaceId: string;
  publicKey: string;
  name: string;
  type: string;
  config?: Record<string, unknown>;
  designConfig?: Record<string, unknown>;
  triggerRules?: unknown[];
  isActive: boolean;
  /** Магазин виджета; null — общий для всех */
  storeId?: string | null;
  embedCode?: string;
}

export function useCreateWidget(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; type: string } & Record<string, unknown>) =>
      apiClient.post<WidgetRow>('/widgets', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets', workspaceSlug] }),
  });
}

export function useUpdateWidget(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<WidgetRow>(`/widgets/${id}`, patch, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets', workspaceSlug] }),
  });
}

export function useDeleteWidget(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/widgets/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets', workspaceSlug] }),
  });
}

// ─── Template mutations ─────────────────────────────────────────────

export function useCreateTemplate(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      kind: 'chat' | 'form' | 'survey';
      category?: string;
      config?: Record<string, unknown>;
    }) => apiClient.post<TemplateRow>('/templates', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates', workspaceSlug] }),
  });
}

export function useUpdateTemplate(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<TemplateRow>(`/templates/${id}`, patch, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates', workspaceSlug] }),
  });
}

export function useDeleteTemplate(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/templates/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates', workspaceSlug] }),
  });
}

// ─── SLA mutations ─────────────────────────────────────────────────

export function useCreateSlaPolicy(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      first_reply_minutes: number;
      next_reply_minutes?: number;
      resolution_minutes?: number;
      is_default?: boolean;
    }) => apiClient.post<SLAPolicy>('/sla-policies', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sla', workspaceSlug] }),
  });
}

// ─── API Keys mutations ─────────────────────────────────────────────

export function useCreateApiKey(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; scope?: string; expires_at?: string }) =>
      apiClient.post<ApiKeyRow & { raw_key: string }>('/api-keys', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys', workspaceSlug] }),
  });
}

export function useRevokeApiKey(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api-keys/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys', workspaceSlug] }),
  });
}

// ─── Webhook mutations ─────────────────────────────────────────────

export function useCreateWebhook(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { url: string; events: string[]; name?: string }) =>
      apiClient.post<WebhookRow>('/webhooks', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', workspaceSlug] }),
  });
}

export function useDeleteWebhook(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/webhooks/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', workspaceSlug] }),
  });
}

// ─── Saved views mutations ──────────────────────────────────────────

export function useCreateSavedView(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      filters: Record<string, unknown>;
      sort?: unknown[];
      icon?: string;
      pinned?: boolean;
    }) => apiClient.post<SavedView>('/saved-views', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-views', workspaceSlug] }),
  });
}

export function useDeleteSavedView(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/saved-views/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-views', workspaceSlug] }),
  });
}

// ─── Billing invoices ──────────────────────────────────────────────

export interface InvoiceRow {
  id: string;
  number: string | null;
  status: string;
  amountMinor: string;
  currency: string;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  pdfUrl: string | null;
}

export function useBillingInvoices(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['billing-invoices', workspaceSlug],
    queryFn: () => apiClient.get<InvoiceRow[]>('/billing/invoices', { workspaceSlug }),
    enabled: !!workspaceSlug,
  });
}

// ─── Operator mutations ─────────────────────────────────────────────

export function useUpdateOperator(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      apiClient.patch<OperatorRow>(`/operators/${id}`, patch, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators', workspaceSlug] }),
  });
}

export function useDeactivateOperator(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/operators/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators', workspaceSlug] }),
  });
}

// ─── Onboarding mutations ───────────────────────────────────────────

export function useUpdateOnboarding(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.patch<unknown>('/workspace/onboarding', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding', workspaceSlug] }),
  });
}

// ─── Workspace mutations ────────────────────────────────────────────

export function useUpdateWorkspace(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      apiClient.patch<{ id: string; name: string; slug: string }>('/workspace', patch, {
        workspaceSlug,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', workspaceSlug] }),
  });
}

/** Код сопряжения для плагина сайта — показывается один раз. */
export function useIssuePairingCode(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (platform: string) =>
      apiClient.post<{ pairing_code: string; expires_in: number }>(
        '/partner/pairing-codes',
        { platform },
        { workspaceSlug },
      ),
  });
}

/** Магазины продавца на связанной платформе. */
export function usePartnerShops(workspaceSlug: string | undefined, linkId: string | undefined) {
  return useQuery({
    queryKey: ['partner-shops', workspaceSlug, linkId],
    queryFn: () =>
      apiClient.get<PartnerShopRow[]>(`/partner/links/${linkId ?? ''}/shops`, { workspaceSlug }),
    enabled: !!workspaceSlug && !!linkId,
    staleTime: 5 * 60 * 1000,
  });
}

/** «Передать в …» — ключ канала уходит на связанную платформу с сервера. */
export function useExportChannel(workspaceSlug: string | undefined) {
  return useMutation({
    mutationFn: (args: { linkId: string; channelId: string; shopId?: string }) =>
      apiClient.post<{ connection_id: string; created: boolean }>(
        `/partner/links/${args.linkId}/export-channel`,
        { channel_id: args.channelId, shop_id: args.shopId },
        { workspaceSlug },
      ),
  });
}

// ─── Магазины внутри пространства ─────────────────────────────────

export interface StoreRow {
  id: string;
  name: string;
  url: string | null;
  is_active: boolean;
  external_ref: string | null;
  /** Есть — магазин связанной платформы (управляется ею) */
  partner_link_id: string | null;
}

export interface StoreSummaryRow {
  /** null — общие каналы без магазина */
  store_id: string | null;
  name: string;
  unassigned: number;
  open: number;
  last_message_at: string | null;
}

export function useStores(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['stores', workspaceSlug],
    queryFn: () => apiClient.get<StoreRow[]>('/stores', { workspaceSlug }),
    enabled: !!workspaceSlug,
    staleTime: 60_000,
  });
}

/**
 * Счётчики для вкладок магазинов. Опрос — потому что событие может прийти
 * в любой магазин, а не только в открытый: вкладка должна загореться сама.
 */
export function useStoreSummary(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['stores-summary', workspaceSlug],
    queryFn: () => apiClient.get<StoreSummaryRow[]>('/stores/summary', { workspaceSlug }),
    enabled: !!workspaceSlug,
    refetchInterval: 15_000,
  });
}

export function useCreateStore(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; url?: string }) =>
      apiClient.post<StoreRow>('/stores', body, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores', workspaceSlug] }),
  });
}

export function useDeleteStore(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: true }>(`/stores/${id}`, { workspaceSlug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores', workspaceSlug] }),
  });
}
